import { lazy, ComponentType } from "react";

/**
 * Wraps React.lazy with chunk load error handling.
 *
 * After a deployment, users with cached bundles may try to load chunks
 * that no longer exist (different hash). This wrapper catches those errors
 * and reloads the page to get the fresh bundle.
 *
 * Uses sessionStorage to prevent infinite reload loops - if we've already
 * tried reloading for this chunk, we let the error propagate to the error boundary.
 */
export function lazyWithRetry<T extends ComponentType>(
  importFn: () => Promise<{ default: T }>,
  chunkName?: string
): React.LazyExoticComponent<T> {
  return lazy(async () => {
    const storageKey = `chunk_reload_${chunkName ?? "unknown"}`;
    const hasReloaded = sessionStorage.getItem(storageKey);

    try {
      const component = await importFn();
      // Success - clear any reload flag
      if (hasReloaded) {
        sessionStorage.removeItem(storageKey);
      }
      return component;
    } catch (error) {
      // Check if this is a chunk load error. Browsers word the failure
      // differently, so match all known variants:
      //   - Chrome/Edge: "Failed to fetch dynamically imported module"
      //   - Firefox:     "error loading dynamically imported module"
      //   - Safari:      "Importing a module script failed"
      //   - webpack:     "Loading chunk" / "Loading CSS chunk"
      const chunkErrorPatterns = [
        "Failed to fetch dynamically imported module",
        "error loading dynamically imported module",
        "Importing a module script failed",
        "Loading chunk",
        "Loading CSS chunk",
      ];
      const isChunkError =
        error instanceof Error &&
        chunkErrorPatterns.some((pattern) => error.message.includes(pattern));

      if (isChunkError && !hasReloaded) {
        // Mark that we're reloading for this chunk
        sessionStorage.setItem(storageKey, "true");
        // Reload to get fresh bundle
        window.location.reload();
        // Return a placeholder while reloading
        return { default: (() => null) as unknown as T };
      }

      // Not a chunk error or already tried reloading - propagate
      throw error;
    }
  });
}
